/**
 * AI Help Chat Store
 *
 * Gestisce sessioni, messaggi e streaming per la chat AI di aiuto.
 * Usa fetch() diretto per streaming SSE (supabase.functions.invoke non supporta streaming).
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import { usePermissionsStore } from './permissionsStore';

export interface AiHelpSession {
  id: string;
  organization_id: string;
  user_id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface AiHelpMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

type WidgetState = 'minimized' | 'sessions' | 'chat';

export interface AiEntityContext {
  type: string;
  id: string;
  summary: Record<string, any>;
}

interface AiHelpStore {
  // Widget
  widgetState: WidgetState;
  setWidgetState: (state: WidgetState) => void;

  // Entity context (set by detail pages)
  entityContext: AiEntityContext | null;
  setEntityContext: (ctx: AiEntityContext | null) => void;

  // Sessions
  sessions: AiHelpSession[];
  currentSessionId: string | null;
  loadingSessions: boolean;

  // Messages
  messages: AiHelpMessage[];
  loadingMessages: boolean;

  // Streaming
  streaming: boolean;
  streamingContent: string;

  // Rate limiting
  lastMessageAt: number;
  rateLimitError: string | null;

  // Actions
  fetchSessions: () => Promise<void>;
  createSession: () => Promise<string | null>;
  deleteSession: (id: string) => Promise<void>;
  selectSession: (id: string) => Promise<void>;
  sendMessage: (content: string, pageContext?: { pathname: string; sectionId?: string; title?: string }) => Promise<void>;
  startNewChat: () => Promise<void>;
}

export const useAiHelpStore = create<AiHelpStore>((set, get) => ({
  widgetState: 'minimized',
  entityContext: null,
  sessions: [],
  currentSessionId: null,
  loadingSessions: false,
  messages: [],
  loadingMessages: false,
  streaming: false,
  streamingContent: '',
  lastMessageAt: 0,
  rateLimitError: null,

  setWidgetState: (state) => set({ widgetState: state }),
  setEntityContext: (ctx) => set({ entityContext: ctx }),

  fetchSessions: async () => {
    set({ loadingSessions: true });
    try {
      const { data, error } = await supabase
        .from('ai_help_sessions')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      set({ sessions: data || [], loadingSessions: false });
    } catch (err) {
      console.error('[aiHelpStore] fetchSessions error:', err);
      set({ loadingSessions: false });
    }
  },

  createSession: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Get organization_id from user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!profile?.organization_id) return null;

      const { data, error } = await supabase
        .from('ai_help_sessions')
        .insert({
          user_id: user.id,
          organization_id: profile.organization_id,
          title: 'Nuova conversazione',
        })
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        sessions: [data, ...state.sessions],
        currentSessionId: data.id,
        messages: [],
      }));

      return data.id;
    } catch (err) {
      console.error('[aiHelpStore] createSession error:', err);
      return null;
    }
  },

  deleteSession: async (id) => {
    try {
      const { error } = await supabase
        .from('ai_help_sessions')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
        currentSessionId: state.currentSessionId === id ? null : state.currentSessionId,
        messages: state.currentSessionId === id ? [] : state.messages,
      }));
    } catch (err) {
      console.error('[aiHelpStore] deleteSession error:', err);
    }
  },

  selectSession: async (id) => {
    set({ currentSessionId: id, loadingMessages: true, messages: [] });
    try {
      const { data, error } = await supabase
        .from('ai_help_messages')
        .select('*')
        .eq('session_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ messages: data || [], loadingMessages: false });
    } catch (err) {
      console.error('[aiHelpStore] selectSession error:', err);
      set({ loadingMessages: false });
    }
  },

  startNewChat: async () => {
    const sessionId = await get().createSession();
    if (sessionId) {
      set({ widgetState: 'chat' });
    }
  },

  sendMessage: async (content, pageContext?) => {
    const { currentSessionId, messages, lastMessageAt } = get();

    // Rate limit: min 3 seconds between messages
    const now = Date.now();
    if (now - lastMessageAt < 3000) {
      set({ rateLimitError: 'Attendi qualche secondo prima di inviare un altro messaggio.' });
      setTimeout(() => set({ rateLimitError: null }), 3000);
      return;
    }
    set({ lastMessageAt: now, rateLimitError: null });

    // Create session on-the-fly if needed
    let sessionId = currentSessionId;
    if (!sessionId) {
      sessionId = await get().createSession();
      if (!sessionId) return;
    }

    // Save user message optimistically
    const userMsg: AiHelpMessage = {
      id: crypto.randomUUID(),
      session_id: sessionId,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };

    const currentMessages = [...get().messages, userMsg];
    set({ messages: currentMessages, streaming: true, streamingContent: '' });

    // Persist user message
    supabase
      .from('ai_help_messages')
      .insert({
        session_id: sessionId,
        role: 'user',
        content,
      })
      .then(({ error }) => {
        if (error) console.error('[aiHelpStore] save user msg error:', error);
      });

    // Get org_id for logging
    const { data: { user } } = await supabase.auth.getUser();
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', user?.id || '')
      .single();

    // Build messages for API (only role + content)
    const apiMessages = currentMessages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Get user's accessible resources for permission-aware AI responses
    const permissionsStore = usePermissionsStore.getState();
    const resourcePermissions = permissionsStore.getResourcePermissions();
    const accessibleResources = Object.keys(resourcePermissions).filter(
      (r) => resourcePermissions[r].size > 0
    );

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error('No auth token');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/ai-help-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          messages: apiMessages,
          session_id: sessionId,
          organization_id: profile?.organization_id || '',
          accessible_resources: accessibleResources,
          current_page: pageContext ? {
            ...pageContext,
            entityContext: get().entityContext || undefined,
          } : undefined,
        }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          const data = await response.json().catch(() => ({}));
          set({ rateLimitError: data.error || 'Hai raggiunto il limite di messaggi. Riprova tra qualche minuto.' });
          setTimeout(() => set({ rateLimitError: null }), 10000);
        }
        throw new Error(`HTTP ${response.status}`);
      }

      // Read SSE stream
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              fullContent += parsed.content;
              set({ streamingContent: fullContent });
            }
          } catch {
            // Skip
          }
        }
      }

      // Save assistant message
      if (fullContent) {
        const assistantMsg: AiHelpMessage = {
          id: crypto.randomUUID(),
          session_id: sessionId,
          role: 'assistant',
          content: fullContent,
          created_at: new Date().toISOString(),
        };

        set((state) => ({
          messages: [...state.messages, assistantMsg],
          streaming: false,
          streamingContent: '',
        }));

        // Persist to DB
        supabase
          .from('ai_help_messages')
          .insert({
            session_id: sessionId,
            role: 'assistant',
            content: fullContent,
          })
          .then(({ error }) => {
            if (error) console.error('[aiHelpStore] save assistant msg error:', error);
          });

        // Update session timestamp + refresh title (auto-generated by edge function)
        supabase
          .from('ai_help_sessions')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', sessionId)
          .then(() => {});

        // After first message, wait a bit for auto-title then refresh session title
        if (currentMessages.filter((m) => m.role === 'user').length <= 1) {
          setTimeout(async () => {
            const { data } = await supabase
              .from('ai_help_sessions')
              .select('title')
              .eq('id', sessionId)
              .single();
            if (data?.title && data.title !== 'Nuova conversazione') {
              set((state) => ({
                sessions: state.sessions.map((s) =>
                  s.id === sessionId ? { ...s, title: data.title } : s
                ),
              }));
            }
          }, 3000);
        }
      } else {
        set({ streaming: false, streamingContent: '' });
      }
    } catch (err) {
      console.error('[aiHelpStore] sendMessage error:', err);
      set({ streaming: false, streamingContent: '' });

      // Add error message
      const errorMsg: AiHelpMessage = {
        id: crypto.randomUUID(),
        session_id: sessionId,
        role: 'assistant',
        content: 'Mi dispiace, si è verificato un errore. Riprova tra qualche istante.',
        created_at: new Date().toISOString(),
      };
      set((state) => ({
        messages: [...state.messages, errorMsg],
      }));
    }
  },
}));
